import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Bell, ArrowRight, X as XIcon } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const DISMISS_KEY = "cogni:dismissed-invites-v1";

/**
 * Persistent banner shown on /dashboard when the freelancer has pending
 * project invitations. Tapping it routes to /my-projects. Dismiss is
 * session-only and per-invitation-id (re-shows if the set of pending IDs
 * changes).
 */
export default function PendingInvitesBanner() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [pending, setPending] = useState([]);
  const [dismissed, setDismissed] = useState(() => {
    try {
      return new Set(JSON.parse(sessionStorage.getItem(DISMISS_KEY) || "[]"));
    } catch { return new Set(); }
  });

  useEffect(() => {
    if (user?.role !== "freelancer") return;
    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await api.get("/me/project-invitations", { params: { status: "Pending" } });
        if (!cancelled) setPending(data || []);
      } catch { /* swallow */ }
    };
    load();
    const t = setInterval(load, 60_000);
    return () => { cancelled = true; clearInterval(t); };
  }, [user?.role]);

  if (user?.role !== "freelancer") return null;
  const visible = pending.filter(p => !dismissed.has(p.id));
  if (visible.length === 0) return null;

  const dismiss = () => {
    const next = new Set([...dismissed, ...visible.map(v => v.id)]);
    setDismissed(next);
    sessionStorage.setItem(DISMISS_KEY, JSON.stringify([...next]));
  };

  const headline = visible.length === 1
    ? `You've been invited to join "${visible[0].project_name}"`
    : `You have ${visible.length} pending project invitations`;

  return (
    <div
      className="mb-5 rounded-md border border-amber-300 bg-amber-50 px-4 py-3 flex items-start sm:items-center gap-3 flex-col sm:flex-row"
      data-testid="banner-pending-invites"
      role="alert"
    >
      <div className="w-9 h-9 rounded-md bg-amber-500 text-white flex items-center justify-center flex-shrink-0">
        <Bell className="w-4 h-4"/>
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-display font-bold text-amber-900 text-sm" data-testid="banner-headline">{headline}</div>
        <div className="text-xs text-amber-800/80 mt-0.5">
          Accept to start picking up tasks, or decline if it's not the right fit.
        </div>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        <Button
          size="sm"
          onClick={() => nav("/my-projects")}
          className="bg-slate-900 hover:bg-slate-800"
          data-testid="banner-cta"
        >
          Review <ArrowRight className="w-3.5 h-3.5 ml-1"/>
        </Button>
        <button
          type="button"
          onClick={dismiss}
          aria-label="Dismiss"
          className="text-amber-700 hover:text-amber-900 p-1.5 rounded-md hover:bg-amber-100 transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
          data-testid="banner-dismiss"
        >
          <XIcon className="w-4 h-4"/>
        </button>
      </div>
    </div>
  );
}
